import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { map, mergeMap, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { MOCK_ROUTES } from 'src/app/components/reports/reports.constants';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import {
  IMAGE_SRC,
  STATUS_OPTIONS,
} from 'src/app/components/shared/shared-constants';
import { ExceptionsReportsService } from 'src/app/core/services/reports/exceptions/exceptions-reports.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { ReportsUtils } from 'src/app/core/utils/reports/reports.utils';
import {
  BuildingScanFormGroup,
  CONSTS,
  REPORT_FILE_NAME,
} from './building-scan-report.constants';

@Component({
  selector: 'app-building-scan-report',
  templateUrl: './building-scan-report.component.html',
  styleUrls: ['./building-scan-report.component.scss'],
  standalone: false,
})
export class BuildingScanReportComponent implements OnInit {
  readonly destroy = inject(DestroyRef);
  readonly input: BuildingScanFormGroup = inject(MAT_DIALOG_DATA);
  private readonly userService = inject(UserService);
  private readonly fb: FormBuilder = inject(FormBuilder);
  private readonly dialogRef: MatDialogRef<BuildingScanReportComponent> =
    inject(MatDialogRef<BuildingScanReportComponent>);
  private readonly commonUtils: CommonUtils = inject(CommonUtils);
  private readonly reportUtils: ReportsUtils = inject(ReportsUtils);
  private readonly exceptionsReportsService: ExceptionsReportsService = inject(
    ExceptionsReportsService,
  );

  readonly CONSTS = CONSTS;
  readonly OPTIONS = MOCK_ROUTES;
  readonly IMAGES = IMAGE_SRC;
  readonly BUTTON_TYPES = BUTTON_TYPES;

  currDate!: Date;
  minDate!: Date;
  maxDate!: Date;
  buildingScanForm!: FormGroup;

  routes = MOCK_ROUTES;

  ngOnInit(): void {
    this.currDate = new Date();
    this.minDate = new Date(
      this.currDate.getFullYear(),
      this.currDate.getMonth(),
      this.currDate.getDate() - 30,
    );

    this.maxDate = new Date(
      this.currDate.getFullYear(),
      this.currDate.getMonth(),
      this.currDate.getDate() + 14,
    );

    this.buildingScanForm = this.fb.group({
      date: [this.currDate],
      routes: [MOCK_ROUTES[0]],
    });

    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        mergeMap((user) => {
          return this.getRouteList(user.location);
        }),
      )
      .subscribe();
  }

  onExit() {
    this.commonUtils.openExitModal(this.buildingScanForm.dirty);
  }

  /**
   * Used to get report in CSV or PDF file format
   *
   * @memberof BuildingScanReportComponent
   */
  getReport() {
    this.userService
      .getCurrentUser$()
      .pipe(
        take(1),

        mergeMap((user) => {
          const formValue = this.buildingScanForm.value;

          const date = this.commonUtils.getFormattedDate(formValue.date);
          const request = {
            locationcode: user?.location,
            date,
            loop: formValue.routes?.value === 'All' ? 'All' : formValue.routes,
          };
          return this.exceptionsReportsService
            .getBuildingScanExportReport(request)
            .pipe(
              take(1),
              map((data) => {
                const buildingScanExport = data.response.buildingScanExport;

                if (!buildingScanExport?.length) {
                  this.commonUtils.updateBannerType(
                    BANNER_TYPE.NO_DATA_AVAILABLE,
                  );
                  return this.dialogRef.close();
                }
                const customHeader = [
                  ['', '', '', `${'Building Scan Export'}`, '', ''],
                ];

                this.reportUtils.exportCSVReport(
                  buildingScanExport,
                  REPORT_FILE_NAME,
                  customHeader,
                );
                return this.dialogRef.close();
              }),
            );
        }),
      )
      .subscribe();
  }

  /**
   * Used to get route list
   *
   * @private
   * @memberof BuildingScanReportComponent
   */
  private getRouteList(code: string) {
    const request = {
      locationcode: code,
    };
    return this.exceptionsReportsService
      .getBuildingScanExportRoutes(request)
      .pipe(
        take(1),
        map((data) => {
          this.routes = data.response.excepProcRoutes
            .map(({ route }) => {
              return {
                label: route,
                value: route,
              };
            })
            .sort((a, b) => a.label.localeCompare(b.label));
          this.routes.unshift({
            label: STATUS_OPTIONS.ALL_SELECTED,
            value: 'All',
          });
        }),
      );
  }
}
